import type { ReactNode } from "react"
import { useCallback, useMemo, useRef, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { createBusyBlock, deleteBusyBlock } from "../../api/busy.api"
import { newIdempotencyKey } from "../../api/idempotency"
import { isApiProblem, transportProblem } from "../../api/problem"
import { formatRange, instantFromLocal, localTimeToMinutes } from "../../journal/journal-time"
import { Button } from "../../ui/button"
import { EmptyState } from "../../ui/empty-state"
import { SkeletonText } from "../../ui/skeleton"
import type { BreakRecovery } from "./master-break"
import { breakOptions, ownBreaks, recoverBreak } from "./master-break"
import styles from "./master-break-screen.module.css"
import { MasterBackBar, MasterFrame, MasterNoEmployee } from "./master-frame"
import type { MasterSearch } from "./use-master-day"
import { useMasterDay } from "./use-master-day"

/**
 * 5-М5 «Перерыв» — закрыть себе время, чтобы туда не записали (ТЗ 05 §5.7).
 *
 * ОДНО КАСАНИЕ. «30 минут прямо сейчас» — это кнопка, а не форма: мастер ставит перерыв
 * между клиентами, мокрыми руками. Кнопки уже знают, что закроют («30 минут · 13:30–14:00»),
 * потому что ближайшее свободное время посчитано заранее из загруженного дня.
 *
 * ПЕРЕРЫВ ИСЧЕЗАЕТ ИЗ СВОБОДНЫХ ОКОН ВИТРИНЫ СРАЗУ: это обычная строка занятости,
 * и подпись под заголовком говорит это прямо — иначе мастер не поймёт, зачем вообще
 * ставить перерыв, если можно просто не брать трубку.
 *
 * ЧЕГО ЗДЕСЬ НЕЛЬЗЯ И ПОЧЕМУ ЭТО ДЕРЖИТ СЕРВЕР. Закрыть время с записью, закрыть чужое
 * время, закрыть время задним числом или вне смены. Экран не прячет эти случаи, а
 * превращает отказ в предложение: «Свободно с 15:45 — поставить там?», «Смена
 * заканчивается в 20:00 — поставить до 20:00?».
 *
 * САЛОН ЗАПРЕТИЛ МАСТЕРАМ ПЕРЕРЫВЫ (нет права busy.manage) — кнопок нет вовсе, и сказано,
 * к кому идти. Это состояние из ТЗ, а не ошибка.
 */
export function MasterBreakScreen(props: { readonly search: MasterSearch }): ReactNode {
  const data = useMasterDay(props.search)
  const canBreak = useHasPermission("busy.manage")
  const [running, setRunning] = useState<string | null>(null)
  const [recovery, setRecovery] = useState<BreakRecovery | null>(null)
  const [note, setNote] = useState("")
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")

  // Ключ идемпотентности живёт с НАМЕРЕНИЕМ «этот интервал», а не с сетевой попыткой:
  // повтор нажатия после потерянного ответа не должен ставить второй перерыв.
  const keys = useRef(new Map<string, string>())
  const keyFor = useCallback((intent: string): string => {
    const known = keys.current.get(intent)
    if (known !== undefined) return known
    const fresh = newIdempotencyKey("break")
    keys.current.set(intent, fresh)
    return fresh
  }, [])

  const result = data.result
  const employeeId = data.employeeId

  const options = useMemo(
    () =>
      result === null || employeeId === null
        ? []
        : breakOptions({ result, date: data.date, employeeId, nowMinute: data.nowMinute }),
    [result, employeeId, data.date, data.nowMinute],
  )
  const breaks = useMemo(
    () =>
      result === null || employeeId === null
        ? []
        : ownBreaks({ result, date: data.date, employeeId, nowMinute: data.nowMinute }),
    [result, employeeId, data.date, data.nowMinute],
  )

  const place = useCallback(
    (startMinute: number, endMinute: number): void => {
      if (result === null || employeeId === null || data.branch === null || running !== null) return
      const intent = `${data.date}:${startMinute}-${endMinute}`
      setRunning(intent)
      setRecovery(null)
      const zone = data.timeZone
      const send = async (): Promise<void> => {
        try {
          await createBusyBlock(
            {
              branchId: data.branch?.id ?? "",
              kind: "break",
              employeeId,
              startAt: instantFromLocal(data.date, startMinute, zone),
              endAt: instantFromLocal(data.date, endMinute, zone),
              ...(note.trim().length === 0 ? {} : { note: note.trim() }),
              acknowledgedWarnings: [],
            },
            keyFor(intent),
          )
          setNote("")
          data.reload()
        } catch (error) {
          const problem = isApiProblem(error) ? error : transportProblem("unknown")
          setRecovery(
            recoverBreak({
              problem,
              attempt: { startMinute, endMinute },
              result,
              date: data.date,
              employeeId,
              nowMinute: data.nowMinute,
            }),
          )
          // Отказ по занятости — повод перечитать день: предложение «свободно с…»
          // должно считаться по свежим данным, а не по тем, на которых человек промахнулся.
          data.reload()
        } finally {
          setRunning(null)
        }
      }
      void send()
    },
    [result, employeeId, data, running, note, keyFor],
  )

  const remove = useCallback(
    (id: string, version: string): void => {
      if (running !== null) return
      setRunning(`remove:${id}`)
      setRecovery(null)
      const send = async (): Promise<void> => {
        try {
          await deleteBusyBlock({ busyId: id, expectedUpdatedAt: version }, keyFor(`remove:${id}:${version}`))
          data.reload()
        } catch (error) {
          const problem = isApiProblem(error) ? error : transportProblem("unknown")
          setRecovery({ text: problem.detail ?? problem.title, retry: null, retryLabel: null })
        } finally {
          setRunning(null)
        }
      }
      void send()
    },
    [running, data, keyFor],
  )

  if (employeeId === null) {
    return (
      <MasterFrame active="day">
        <MasterNoEmployee title="Перерыв" />
      </MasterFrame>
    )
  }

  if (!canBreak) {
    return (
      <MasterFrame active="day">
        <MasterBackBar to="/my/day" label="Мой день" />
        <EmptyState
          className={styles.gap}
          title="Перерывы в этом салоне ставит администратор"
          text="Попросите администратора закрыть для вас время в журнале — клиенты не смогут записаться на него."
        />
      </MasterFrame>
    )
  }

  const canEdit = data.availability.canEdit
  const denied = canEdit ? undefined : (data.availability.deniedReason ?? undefined)
  const customStart = localTimeToMinutes(from.length === 0 ? null : from)
  const customEnd = localTimeToMinutes(to.length === 0 ? null : to)
  const customValid = customStart !== null && customEnd !== null && customEnd > customStart

  return (
    <MasterFrame active="day" stripe={data.availability.stripe} onStripeAction={data.reload}>
      <MasterBackBar to="/my/day" label="Мой день" />

      <div className={styles.body}>
        <div>
          <h1 className={styles.title}>Перерыв</h1>
          <p className={styles.sub}>
            Время сразу закроется для записи на сайте и в ботах. Клиентов на него не запишут.
          </p>
        </div>

        {result === null ? (
          <SkeletonText widths={[220, 220, 220]} height={44} />
        ) : options.length === 0 ? (
          <p className={styles.sub}>До конца смены свободного времени для перерыва нет.</p>
        ) : (
          <div className={styles.options}>
            {options.map((option) => (
              <Button
                key={option.minutes}
                size="touch"
                variant={option.minutes === 30 ? "primary" : "secondary"}
                className={styles.option}
                loading={running === `${data.date}:${option.startMinute}-${option.endMinute}`}
                loadingLabel="Закрываем время…"
                deniedReason={denied}
                onClick={() => {
                  place(option.startMinute, option.endMinute)
                }}
              >
                {option.label}
              </Button>
            ))}
          </div>
        )}

        {recovery === null ? null : (
          <div className={styles.recovery} role="alert">
            <p className={styles.recoveryText}>{recovery.text}</p>
            {recovery.retry === null || recovery.retryLabel === null ? null : (
              <Button
                size="touch"
                variant="primary"
                deniedReason={denied}
                onClick={() => {
                  if (recovery.retry !== null) place(recovery.retry.startMinute, recovery.retry.endMinute)
                }}
              >
                {recovery.retryLabel}
              </Button>
            )}
          </div>
        )}

        <fieldset className={styles.custom}>
          <legend className={styles.legend}>Другое время</legend>
          <div className={styles.times}>
            <label className={styles.field}>
              <span>с</span>
              <input
                type="time"
                step={300}
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value)
                }}
              />
            </label>
            <label className={styles.field}>
              <span>до</span>
              <input
                type="time"
                step={300}
                value={to}
                onChange={(event) => {
                  setTo(event.target.value)
                }}
              />
            </label>
          </div>
          <label className={styles.field}>
            <span>Причина — её увидит администратор (необязательно)</span>
            <input
              type="text"
              maxLength={500}
              value={note}
              placeholder="Обед, за материалами…"
              onChange={(event) => {
                setNote(event.target.value)
              }}
            />
          </label>
          <Button
            size="touch"
            disabled={!customValid}
            deniedReason={denied}
            onClick={() => {
              if (customStart !== null && customEnd !== null) place(customStart, customEnd)
            }}
          >
            {customValid ? `Закрыть ${formatRange(customStart, customEnd)}` : "Закрыть время"}
          </Button>
        </fieldset>

        {breaks.length === 0 ? null : (
          <section>
            <h2 className={styles.section}>Мои перерывы сегодня</h2>
            <ul className={styles.list}>
              {breaks.map((item) => (
                <li key={item.id} className={styles.item}>
                  <span>
                    {formatRange(item.startMinute, item.endMinute)}
                    {item.note === null ? "" : ` · ${item.note}`}
                  </span>
                  {item.past ? (
                    <span className={styles.muted}>прошёл</span>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={running === `remove:${item.id}`}
                      loadingLabel="Снимаем…"
                      deniedReason={denied}
                      onClick={() => {
                        remove(item.id, item.version)
                      }}
                    >
                      Снять
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </MasterFrame>
  )
}
